import React, { useEffect, useState, useCallback, useRef, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppBar, Toolbar, Button, Box, Container, useMediaQuery} from '@mui/material';
import BackgroundPattern from './components/BackgroundPattern'; // Ensure this path is correct
import AnimatedText from './components/AnimatedText';
import {gsap} from 'gsap';
import HideOnScroll from './components/hideOnScroll';
import ScrollTrigger from 'gsap/ScrollTrigger';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
import AnimationOverlay from './components/AnimationOverlay';
import './App.css'; // Import the App.css styles

// Use lazy to load components that are not immediately visible
const TwitterImageList = React.lazy(() => import('./components/TwitterImageList'));
const Lan = React.lazy(() => import('./components/Lan'));
const LampPaint = React.lazy(() => import('./components/LampPaint'));
const IllAirbrush = React.lazy(() => import('./components/iLLAirbrush'));
const TokenomicsSection = React.lazy(() => import('./components/TokenomicsSection'));


gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

function App() {
  const scrollToSection = useCallback((sectionId) => {
    const sections = ['#home', '#LAN', '#twitter', '#tokenomics'];
    if (sections.includes(`#${sectionId}`)) {
      gsap.to(window, {
        duration: 1, // Adjust duration as needed
        scrollTo: { y: `#${sectionId}`, offsetY: 0 },
        ease: "power2.inOut",
      });
    } else {
      console.error(`${sectionId} is not a valid section or included in the scroll list.`);
    }
  }, []);
  
  
  // State to control the visibility of the animation overlay
  const [isAnimationDone, setIsAnimationDone] = useState(false);
  const contentRef = useRef(null);
  const LANref = useRef(null); // Ref for the LAN section
  const twitterRef = useRef(null); // Ref for the Twitter section
  const tokenomicsRef = useRef(null); // Ref for the Tokenomics section
  const backgroundRef = useRef(null); // Initialize the ref

  // Determines if the screen size is small (e.g., mobile devices)
  const isSmallScreen = useMediaQuery('(max-width:600px)');

  const [totalHeight, setTotalHeight] = useState(0);

  // TOTAL HEIGHT CALCULATION
  useEffect(() => {
    const calculateTotalHeight = () => {
      if (contentRef.current) {
        const contentHeight = contentRef.current.getBoundingClientRect().height;
        setTotalHeight(contentHeight);
      }
    };

    calculateTotalHeight();

    window.addEventListener('resize', calculateTotalHeight);
    window.addEventListener('scroll', calculateTotalHeight);

    return () => {
      window.removeEventListener('resize', calculateTotalHeight);
      window.removeEventListener('scroll', calculateTotalHeight);
    };
  }, []);

  // Callback function that updates the state to hide the overlay
  const handleAnimationDone = useCallback(() => {
    setIsAnimationDone(true); // This will trigger the overlay to fade out
  }, []);


  //ADD GSAP PINNING AND SNAPPING
  useEffect(() => {
    // Add only sections that need pinning
    const sections = ['#home', '#LAN', '#twitter', '#tokenomics'];
  
    const scrollTriggers = sections.map(selector => {
      const element = document.querySelector(selector);
  
      return ScrollTrigger.create({
        normalizeScroll: true,
        trigger: element,
        start: 'top top',
        end: 'bottom top',
        refreshPriority: -9,
        anticipatePin: 2,
        pin: true, // Pins the entire section
        scrub: 1,
      });
    });
  
    // Cleanup on unmount for all scroll triggers
    return () => {
      scrollTriggers.forEach(trigger => trigger.kill());
    };
  }, []);

  // INTRO TEXT ANIMATION
  useEffect(() => {
    // Ensure elements are available in the DOM
    gsap.utils.toArray('.paragraph p').forEach(p => {
      let words = p.innerText.split(' ');
      let html = words.reduce((acc, word) => {
        // Wrap each word with span
        return acc + `<span class='line' style='--highlight-offset: 0%;'>${word} </span>`;
      }, "");
      p.innerHTML = html; // Replace paragraph content
    });

    gsap.set(".paragraph", {opacity: 1});
    const section = document.querySelector('.actual-section'); 

    const tl = gsap.timeline({
      scrollTrigger: {
        // pinSpacing: false, // You almost always want pinSpacing: true
        trigger: ".actual-section",
        scrub: 1,
        start: () => "top top+=" + (section.getBoundingClientRect().top - 10 - window.scrollY), // Starts 10px above the paragraph
        end: () => "bottom top+=" + (section.getBoundingClientRect().top - 10 - window.scrollY), // Starts 10px above the paragraph
        pin: ".background",
        // Snap to the next section after completion
         
        onLeave: () => {
          gsap.to(window, { duration: 1.5, scrollTo: "#LAN", ease:"power2.inOut" }); // Use GSAP ScrollToPlugin
        },
       /*
        onLeave: () => {
          document.querySelector("#LAN").scrollIntoView({ behavior: "smooth" });
        }
        */
        //markers: true
      }
    });
    tl.to(".line", {
      "--highlight-offset": "100%",
      stagger: 0.5,
    });

  }, []);
  
  useEffect(() => {
    // Ensure elements are available in the DOM
    gsap.utils.toArray('.paragraph2 p').forEach(p => {
      let words = p.innerText.split(' ');
      let html = words.reduce((acc, word) => {
        // Wrap each word with span
        return acc + `<span class='line2' style='--highlight-offset: 0%;'>${word} </span>`;
      }, "");
      p.innerHTML = html; // Replace paragraph content//
    });

    gsap.set(".paragraph2", {opacity: 1});
    const section = document.querySelector('.actual-section'); 

    const tl = gsap.timeline({
      scrollTrigger: {
        // pinSpacing: false, // You almost always want pinSpacing: true
        trigger: ".actual-section",
        scrub: 1,
        start: () => "top top+=" + (section.getBoundingClientRect().top - 10 - window.scrollY), // Starts 10px above the paragraph
        end: () => "bottom top+=" + (section.getBoundingClientRect().top - 10 - window.scrollY), // Starts 10px above the paragraph
        pin: ".background",
        //markers: true
      }
    });
    tl.to(".line2", {
      "--highlight-offset": "100%",
      stagger: 0.5,
    });

  }, []);


  // Fade-in effect for the LAN/Twitter/Tokenomics section
  useEffect(() => {

    if (LANref.current) {
      gsap.fromTo(LANref.current, 
        { opacity: 0 }, 
        { opacity: 1, 
          duration: .5,
          scrollTrigger: {
          trigger: LANref.current,
          refreshPriority:-12,
          start: 'top', // Trigger the animation when the top of the element is 20% from the top of the viewport
          end: 'bottom 60%',
          toggleActions: 'play none none reverse',
          markers: false
        }
      });
    }

    if (twitterRef.current) {
      gsap.fromTo(twitterRef.current, 
        { opacity: 0 }, 
        { opacity: 1, 
          duration: .5,
          scrollTrigger: {
          trigger: twitterRef.current,
          refreshPriority:-13,
          start: 'top ', // Trigger the animation when the top of the element is 20% from the top of the viewport
          end: 'bottom 60%',
          toggleActions: 'play none none reverse'
        }
      });
    }
    
    
    if (tokenomicsRef.current) {
      gsap.fromTo(tokenomicsRef.current, 
        { opacity: 0 }, 
        { opacity: 1, 
          duration: .5,
          scrollTrigger: {
          trigger: tokenomicsRef.current,
          refreshPriority:-14,
          start: 'top ', // Trigger the animation when the top of the element is 20% from the top of the viewport
          end: 'bottom 60%',
          toggleActions: 'play none none reverse'
        }
      });
    }
  }, []);
  

  return (
    <Router>
      <BackgroundPattern ref={backgroundRef} totalHeight={totalHeight} /> {/* Render the background pattern */}
      <div ref={contentRef}>
      {isAnimationDone && (
        <HideOnScroll>
          <AppBar position="fixed" sx={{background:'none', boxShadow:'none'}}>
            <Toolbar>
              {/* Navigation Buttons on the Right */}
              {/* Wrap buttons in a Box with justifyContent to align them to the right */}
              <Box sx={{ marginLeft: 'auto' }}> {/* This pushes the buttons to the right */}
                <Button style={{fontFamily: 'colfax-web', fontWeight: '600', color: '#d4aa2d', opacity:'.8'}} color="inherit" onClick={() => scrollToSection('home')}>Home</Button>
                <Button style={{fontFamily: 'colfax-web', fontWeight: '600', color: '#d4aa2d', opacity:'.8'}} color="inherit" onClick={() => scrollToSection('twitter')}>Socials</Button>
                <Button style={{fontFamily: 'colfax-web', fontWeight: '600', color: '#d4aa2d', opacity:'.8'}} color="inherit" onClick={() => scrollToSection('tokenomics')}>launchpad</Button>
              </Box>
            </Toolbar>
          </AppBar>
        </HideOnScroll>
      )}
      {/* Add padding to avoid content being hidden behind the AppBar */}
      <Toolbar style={{padding:'0', position:"fixed"}}>
        <Button onClick={() => scrollToSection('home')} style={{padding: 0, minWidth: '10vw', left:'0'}}>
          <Box
            component="img"
            sx={{
              height: 'clamp(1.5rem, 4vw, 2.5rem)',  // Adjust based on your logo's size
            }}
            src={`${process.env.PUBLIC_URL}/lamp-icon-bw2.svg`}
            alt="Logo"
          />
        </Button>
      </Toolbar>
      <Container
          id="home" 
          maxWidth='false' disableGutters
          sx={{ paddingTop: '64px', mb: '64px', minHeight: '100vh'}} // Add padding to avoid content being hidden behind the AppBar
        > {/* marginTop matches AppBar height */}
        <Container 
        maxWidth='false' disableGutters
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: '9999', position: 'relative', height: 'auto', width: '80%'}}
        >
          {!isAnimationDone && !isSmallScreen && (
            <div component="div" style={{ flexGrow: 1, textAlign: 'left', position: 'absolute', left:'0', bottom: '1vh', color:'#d4aa2d'}} className="blinking-text">
              Loading
            </div>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 'auto', }}>
            <AnimatedText text="i love $lamp" onAnimationDone={handleAnimationDone} style={{ width: '100%', height: 'auto'}}/>
          </Box>  
        </Container>
        <AnimationOverlay show={!isAnimationDone} /> {/* Render the overlay based on the state */}
        <Container id="intro" className='actual-section' maxWidth='false' disableGutters
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'auto', width: '80%'}}>
          <Box className='paragraph-container' sx={{ position: 'relative', width: '100%', height: 'auto', margin: 'auto', marginTop:'2vh'}}>
            <Box className='paragraph' sx={{ position: 'relative', width: '100%', height: 'auto', top: 0, left: 0}}>
                <p>Introducing $LAMP coin, a heartfelt homage to the countless souls worldwide who share a secret adoration for lamps. $LAMP transcends the ordinary memecoin; it's a beacon of affection that unites millions of $LAMPers globally - whether flesh and bone or circuits and chrome. Join us in celebrating the light of $LAMP, where every transaction illuminates the bonds of a community where love and currency converge in the digital ether.</p>
            </Box>
            <Box className='paragraph2' sx={{ position: 'absolute', width: '100%', height: 'auto', top: 0, left: 0}}>
                <p>Introducing $LAMP coin, a heartfelt homage to the countless souls worldwide who share a secret adoration for lamps. $LAMP transcends the ordinary memecoin; it's a beacon of affection that unites millions of $LAMPers globally - whether flesh and bone or circuits and chrome. Join us in celebrating the light of $LAMP, where every transaction illuminates the bonds of a community where love and currency converge in the digital ether.</p>
            </Box>
            <Box className='paragraph-hashtag' sx={{ position: 'relative', width: '100%', height: 'auto'}}>
                <p>#ilove$lamp&nbsp;&nbsp;&nbsp;&nbsp;#digitalheartsmatter</p>
            </Box>
          </Box>
        </Container>
      </Container>
    <div id="LAN" ref={LANref} style={{marginBottom:'25%', minHeight:'100vh', paddingTop: '64px',}}>
        <Container sx={{ width:'80%'}} 
        maxWidth='false' disableGutters>
          <Box sx={{ position: 'relative', zIndex:'-10'}}>
            {/* Wrap other lazy components similarly */}
            <Suspense fallback={<div>Loading Lan...</div>}>
              <Lan backgroundRef={backgroundRef} />
            </Suspense>
          </Box>
        </Container>
        <Suspense fallback={<div>Loading Lamp Paint...</div>}>
          <LampPaint />
        </Suspense>
        <Box sx={{alignItems:'center', display: 'flex', justifyContent: 'center', position: 'relative', mt:"2rem"}}>
          <Suspense fallback={<div>Loading Airbrush...</div>}>
            <IllAirbrush />
          </Suspense>
         { /*<img src={`${process.env.PUBLIC_URL}/i-love-lamp-airbrush.png`} alt="iLL" style={{ width: '80%', opacity:'.7', color: '#634a00', mixBlendMode:'exclusion'}} />*/}
        </Box>
    </div>
    <div id='twitter' ref={twitterRef}  style={{ position: 'relative'}}>
      <Container 
        maxWidth={false} disableGutters
        sx={{ 
            position: 'relative', 
            display: 'flex', 
            flexDirection: 'row', 
            alignItems: 'center', 
            justifyContent: 'space-between',  // Ensure child items are spaced out
            width: '80%',
            mb: '15vw', // Add margin to the bottom to avoid content being hidden behind the AppBar 
        }}>
        <Box item sx={{ display: 'flex', ml: '0', alignItems: 'center' }}>
          <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%" height="auto"
          >
            <defs>
              <filter id="textShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feOffset in="SourceAlpha" dx="3" dy="3" result="offsetBlur" />
                <feGaussianBlur in="offsetBlur" stdDeviation="3" result="blur" />
                <feFlood floodColor="rgba(253, 225, 141,.75)" result="colorBlur" /> 
                <feComposite in="colorBlur" in2="blur" operator="in" result="shadow" />
                <feMerge>
                  <feMergeNode in="shadow" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>

              <mask id="text-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                <text
                  x="0%" y="80%"
                  textAnchor="start"
                  dominantBaseline="auto"
                  fontFamily="'colfax-web', sans-serif"
                  fontSize="clamp(3rem, 8vw, 5rem)" /* Adjust the font size */
                  fontWeight="500"
                  fill="black"
                >
                  socials
                </text>
              </mask>
            </defs>
            <text
              x="0%" y="80%"
              textAnchor="start"
              dominantBaseline="auto"
              fontFamily="'colfax-web', sans-serif"
              fontSize="clamp(3rem, 8vw, 5rem)" /* Adjust the font size */
              fontWeight="500"
              fill="#634a00"
              mask="url(#text-mask)"
              filter="url(#textShadow)"
            >
              socials
            </text>
          </svg>
        </Box>
      </Container>
      <Container sx={{ width:'100%', overflowX:'clip', paddingBottom:'20vh'}} maxWidth='false' disableGutters >
          <Box sx={{ 
              position: 'relative', 
              width: '100%', 
              height: 'auto', 
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center'}}>
              {/* Use Suspense for lazy loaded components with a fallback */}
              <Suspense fallback={<div>Loading...</div>}>
                <TwitterImageList accountName="ilovelampcoin" />
              </Suspense>
          </Box>
        </Container>
      </div>
      <TokenomicsSection tokenomicsRef={tokenomicsRef} />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <Routes>
        <Route path="/about"/>
        <Route path="/" />
        <Route path="/resources"/>
      </Routes>
    </div>
    </Router>
  );
}
export default App;